import PropTypes from "prop-types";
import { useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { get } from "lodash";
import { PaperclipIcon, XIcon } from "lucide-react";
import { toast } from "sonner";

import {
  Attachment,
  AttachmentAction,
  AttachmentActions,
  AttachmentContent,
  AttachmentMedia,
  AttachmentTitle,
} from "@/components/ui/attachment";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { formatError } from "helpers/formatError";
import AttachmentService from "services/Attachment";

import { REQUIRED_MARK } from "./fieldClasses";

/**
 * Attachment picker for BaseForm. The stored value is an array of uploaded
 * attachment objects (`{ s3_key, file_name, file_size, file_content_type }`),
 * the shape the email and activity endpoints accept, so a consumer can submit
 * `field.value` unchanged. Files upload to S3 on selection through
 * `AttachmentService.uploadFile`; the input is disabled while an upload is
 * pending.
 */
const FormFileUpload = ({
  name,
  label,
  required,
  disabled,
  id,
  buttonText = "Attach Files",
}) => {
  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name);
  const inputId = id ?? name;
  const inputRef = useRef(null);

  // Each file is uploaded independently so one failure does not discard the
  // uploads that succeeded. The result lists both the uploaded attachments and
  // the failed files.
  const uploadMutation = useMutation({
    mutationFn: async selected => {
      const results = await Promise.allSettled(
        selected.map(file => AttachmentService.uploadFile(file))
      );
      const uploaded = [];
      const failed = [];
      results.forEach((result, index) => {
        if (result.status === "fulfilled") uploaded.push(result.value);
        else failed.push({ file: selected[index], reason: result.reason });
      });
      return { uploaded, failed };
    },
  });

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const files = field.value;
        const isUploading = uploadMutation.isPending;

        const handleSelect = event => {
          const selected = [...event.target.files];
          // Reset so the same file can be re-picked after being removed.
          event.target.value = "";
          if (selected.length === 0) return;
          uploadMutation.mutate(selected, {
            onSuccess: ({ uploaded, failed }) => {
              if (uploaded.length > 0) {
                // Read the current value: a file removed during the upload is
                // not in the list captured when the files were selected.
                field.onChange([...getValues(name), ...uploaded]);
              }
              if (failed.length > 0) {
                const names = failed.map(item => item.file.name).join(", ");
                toast.error(
                  formatError(failed[0].reason, `Could not upload ${names}`)
                );
              }
            },
          });
        };

        const remove = index =>
          field.onChange(files.filter((_, i) => i !== index));

        return (
          <Field data-invalid={error ? true : undefined}>
            {label && (
              <FieldLabel
                htmlFor={inputId}
                className={required ? REQUIRED_MARK : undefined}
              >
                {label}
              </FieldLabel>
            )}
            <div className="tw:flex tw:flex-col tw:gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="tw:self-start"
                disabled={disabled || isUploading}
                onClick={() => inputRef.current?.click()}
              >
                <PaperclipIcon data-icon="inline-start" />
                {isUploading ? "Uploading…" : buttonText}
              </Button>
              <input
                ref={inputRef}
                id={inputId}
                type="file"
                multiple
                className="tw:sr-only"
                aria-label={label ? undefined : "Attachments"}
                aria-required={required || undefined}
                aria-invalid={error ? true : undefined}
                disabled={disabled || isUploading}
                onChange={handleSelect}
              />
              {files.length > 0 && (
                <div className="tw:flex tw:flex-col tw:gap-2">
                  {files.map((file, index) => (
                    <Attachment key={`${file.s3_key}-${index}`}>
                      <AttachmentMedia>
                        <PaperclipIcon />
                      </AttachmentMedia>
                      <AttachmentContent>
                        <AttachmentTitle>{file.file_name}</AttachmentTitle>
                      </AttachmentContent>
                      <AttachmentActions>
                        <AttachmentAction
                          aria-label={`Remove ${file.file_name}`}
                          onClick={() => remove(index)}
                        >
                          <XIcon />
                        </AttachmentAction>
                      </AttachmentActions>
                    </Attachment>
                  ))}
                </div>
              )}
            </div>
            <FieldError>{error?.message}</FieldError>
          </Field>
        );
      }}
    />
  );
};

FormFileUpload.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  buttonText: PropTypes.string,
};

export default FormFileUpload;
